import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { map, exhaustMap, catchError, finalize } from 'rxjs/operators';
import { BannersService } from '../../_services/banners.service';
import { BannersActions } from '../_actions/banners.actions';
import { LayoutActions } from '../_actions/layout.actions';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { AppState } from '../index';
import {
  LayoutUtilsServices,
  MessageType,
} from '../../_services/layout-utils.services';
import { currentBannersFilters } from '../_selectors/banners.selectors';

@Injectable()
export class BannersEffects {
  loadBanners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BannersActions.loadBanners),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._bannersService.findAll(action.filters).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) =>
            BannersActions.loadBannersSuccess({
              items: res.data,
              total: res.total,
              activeCount: res.activeCount || 0,
            })
          ),
          catchError(() => EMPTY)
        );
      })
    )
  );

  loadBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BannersActions.loadBanner),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._bannersService.findOne(action.id).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((data) => BannersActions.loadBannerSuccess({ data })),
          catchError(() => EMPTY)
        );
      })
    )
  );

  addBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BannersActions.addBanner),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._bannersService.create(action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Banner creado exitosamente',
              type: MessageType.Susses,
            });
            this.router.navigate(['/banners']);
            this.store.dispatch(
              BannersActions.loadBanners({ filters: this.filters })
            );
            return BannersActions.addBannerSuccess({ data: res });
          }),
          catchError(() => {
            this._layoutUtilsServices.showNotification({
              message: 'Error al crear el banner, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  editBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BannersActions.editBanner),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._bannersService.update(action.id, action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Banner actualizado exitosamente',
              type: MessageType.Susses,
            });
            this.router.navigate(['/banners']);
            return BannersActions.editBannerSuccess({ id: action.id, data: res });
          }),
          catchError(() => {
            this._layoutUtilsServices.showNotification({
              message: 'Error al actualizar el banner, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  /**
   * Effect para activar/desactivar banner.
   * IMPORTANTE: Recarga la lista después del toggle para actualizar el contador de activos (activeCount).
   * Maneja error del backend cuando se intenta activar un 4to banner (máximo 3 permitidos).
   */
  toggleBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BannersActions.toggleBanner),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._bannersService.toggle(action.id, action.active).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: `Banner ${action.active ? 'activado' : 'desactivado'} exitosamente`,
              type: MessageType.Susses,
            });
            // Recargar lista para actualizar activeCount
            this.store.dispatch(
              BannersActions.loadBanners({ filters: this.filters })
            );
            return BannersActions.toggleBannerSuccess({ data: res });
          }),
          catchError((error) => {
            this._layoutUtilsServices.showNotification({
              message: error?.error?.message || 'Error al cambiar estado del banner',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  removeBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BannersActions.removeBanner),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._bannersService.remove(action.id).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Banner eliminado exitosamente',
              type: MessageType.Susses,
            });
            this.store.dispatch(
              BannersActions.loadBanners({ filters: this.filters })
            );
            return BannersActions.removeBannerSuccess({ id: action.id });
          }),
          catchError(() => {
            this._layoutUtilsServices.showNotification({
              message: 'Error al eliminar el banner, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  filters: any = {};
  constructor(
    private actions$: Actions,
    private _bannersService: BannersService,
    private _layoutUtilsServices: LayoutUtilsServices,
    private store: Store<AppState>,
    private router: Router
  ) {
    this.store
      .pipe(select(currentBannersFilters))
      .subscribe((data) => (this.filters = data));
  }
}
